/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/camelcase */
import { Grid, Typography, makeStyles } from '@material-ui/core';
import AccessAlarmIcon from '@material-ui/icons/AccessAlarm';
import ToggleCard from 'components/ToggleCard';
import i18n from 'i18n';
import { BlockedPeriodsProps } from 'modules/clients/interfaces';
import React from 'react';
import { connect } from 'react-redux';
import GenericForm from 'skyone-lib/lib/GenericForm';
import ApplicationState from 'store/interfaces';
import { applyI18n, cyrb53Hash } from 'utils/helpers';
import DatePicker from 'components/DatePicker';
import {
  CompanyData,
  PermissionsData,
  RolesEnum,
} from 'modules/authenticate/interfaces';
import clsx from 'clsx';
import { inAdminView, inSuperAdminView, inOperatorView } from 'utils/role';
import screenFields from './formFields';
import { initial } from './initialValues';
import SelectWeekDays from './selectWeekDays';
import styles from './styles';

const useStyles = makeStyles(styles);
interface FormProps {
  active: boolean;
  loading?: boolean;
  blockedPeriods: BlockedPeriodsProps[];
  permissions: PermissionsData | undefined;
  role?: RolesEnum;
  companySelected: CompanyData | null;
  onChange: (
    values: BlockedPeriodsProps[],
    control_by_schedule: boolean
  ) => void;
}

const Form = ({
  active,
  loading,
  blockedPeriods,
  role,
  companySelected,
  permissions,
  onChange,
}: FormProps) => {
  const classes = useStyles();
  const [values, setValues] = React.useState<BlockedPeriodsProps[]>();
  const [isActive, setIsActive] = React.useState<boolean>(!!active);

  const setFieldValue = (field: string, value: any, index: number) => {
    const newValues = [...values!];
    newValues[index][field] = value;

    setValues(newValues);
    onChange(newValues, isActive);
  };

  const getBlockedPeriods = (): BlockedPeriodsProps[] => {
    if (blockedPeriods?.length === 2) return blockedPeriods;
    if (blockedPeriods?.length) return [...blockedPeriods, initial[0]];
    return initial;
  };

  React.useEffect(() => {
    const items = getBlockedPeriods();

    const getHours = (hours: string) => {
      const [hour, minutes, seconds] = hours.split(':');
      const now = new Date();

      return new Date(
        now.setHours(Number(hour), Number(minutes), Number(seconds))
      ).toString();
    };

    const newValues = items.map(item => ({
      ...item,
      start_restriction_at: getHours(item?.start_restriction_at),
      stop_restriction_at: getHours(item?.stop_restriction_at),
    }));

    setValues(newValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blockedPeriods]);

  return (
    <ToggleCard
      disable={!!permissions && !permissions.client_access_control}
      id="toggle-restriction"
      active={active}
      title={i18n.t('BY_TIME_KEY')}
      icon={
        <AccessAlarmIcon
          className={clsx({
            [classes.iconGreen]: inAdminView(role, companySelected),
            [classes.iconGray]: inSuperAdminView(role, companySelected),
            [classes.iconBlue]: inOperatorView(role, companySelected),
            [classes.iconYellow]: companySelected,
          })}
        />
      }
      getActive={status => {
        setIsActive(!status);
        onChange(values!, !status);
      }}
    >
      <Grid container spacing={2} className={classes.formContainer}>
        {!loading &&
          values?.map((item, index) => (
            <Grid
              key={cyrb53Hash(String(index))}
              item
              container
              spacing={2}
              xs={6}
              justify="center"
            >
              <Grid item xs={12}>
                <Typography className={classes.periodText}>
                  {i18n.t(`RESTRICTION_RULE_${index + 1}_KEY`)}
                </Typography>
              </Grid>

              <Grid justify="space-around">
                <SelectWeekDays
                  defaultValue={item?.weekdays}
                  onChange={value => setFieldValue('weekdays', value, index)}
                />
              </Grid>

              <Grid className={classes.datePickerContainer} item spacing={2}>
                <DatePicker
                  id="start_restriction_at"
                  type="time"
                  label={i18n.t('START_RESTRICTION_AT_KEY')}
                  keyValue="start_restriction_at"
                  value={item?.start_restriction_at}
                  setFieldsValue={(key, value) =>
                    setFieldValue(key, value, index)
                  }
                />
              </Grid>
              <Grid className={classes.datePickerContainer} item spacing={2}>
                <DatePicker
                  id="stop_restriction_at"
                  type="time"
                  label={i18n.t('END_RESTRICTION_AT_KEY')}
                  keyValue="stop_restriction_at"
                  value={item?.stop_restriction_at}
                  minDate={item?.start_restriction_at}
                  setFieldsValue={(key, value) =>
                    setFieldValue(key, value, index)
                  }
                />
              </Grid>
              <GenericForm
                errors={[]}
                values={item}
                setFieldValue={(field, value) =>
                  setFieldValue(field, value, index)
                }
                screenFields={applyI18n(screenFields, i18n.language)}
              />
            </Grid>
          ))}
      </Grid>
    </ToggleCard>
  );
};

const mapStateToProps = ({
  authenticate: { data },
  clients: {
    accessControl: { loading, blockedPeriods },
  },
}: ApplicationState) => ({
  loading,
  blockedPeriods: blockedPeriods.results,
  permissions: data?.permissions,
});

export default connect(mapStateToProps)(Form);
